import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, fmtDate, serviceLabel } from "../lib/api";
import { TopBar } from "../components/TopBar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Search, Plus, DollarSign, AlertTriangle, CheckCircle2, ChevronRight,
  FileText, Loader2, Clock,
} from "lucide-react";

const FILTERS = [
  { key: "all", label: "All", testid: "filter-all-btn" },
  { key: "unpaid", label: "Unpaid", testid: "filter-unpaid-btn" },
  { key: "overdue", label: "Overdue", testid: "filter-overdue-btn" },
  { key: "paid", label: "Paid", testid: "filter-paid-btn" },
];

function StatusPill({ inv }) {
  if (inv.status === "paid")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#D1FAE5] text-[#059669]">
        <CheckCircle2 className="h-3 w-3" /> Paid
      </span>
    );
  if (inv.overdue)
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#FEE2E2] text-[#DC2626] ring-1 ring-[#DC2626]/30">
        <AlertTriangle className="h-3 w-3" /> Overdue
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#B45309]">
      <Clock className="h-3 w-3" /> Unpaid
    </span>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    amber: "text-[#D97706] bg-[#FEF3C7]",
    red: "text-[#DC2626] bg-[#FEE2E2]",
    green: "text-[#059669] bg-[#D1FAE5]",
  };
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold truncate">{label}</p>
        <p className="font-mono-data font-bold text-lg text-slate-900 truncate">{value}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, st] = await Promise.all([
        api.get("/invoices", { params: { q, status: filter } }),
        api.get("/invoices/stats"),
      ]);
      setInvoices(inv.data);
      setStats(st.data);
    } finally {
      setLoading(false);
    }
  }, [q, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F8F6F0]">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h1 className="font-headline text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Invoices</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {stats ? `${stats.total_invoices} total` : "Loading…"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatCard testid="stat-unpaid-balance" icon={DollarSign} tone="amber" label="Unpaid Balance"
            value={stats ? money(stats.unpaid_balance) : "—"} />
          <StatCard testid="stat-overdue-count" icon={AlertTriangle} tone="red" label="Overdue"
            value={stats ? stats.overdue_count : "—"} />
          <StatCard testid="stat-paid-month" icon={CheckCircle2} tone="green" label="Paid This Month"
            value={stats ? money(stats.paid_this_month) : "—"} />
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            data-testid="invoice-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, invoice #, address, phone…"
            className="h-12 pl-11 text-base bg-white border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20"
          />
        </div>

        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={f.testid}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${
                filter === f.key
                  ? "bg-[#1B3B2B] text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-[#1B3B2B]/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-[#1B3B2B]" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
            <FileText className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 font-medium">No invoices found</p>
            <Button
              data-testid="empty-new-invoice-button"
              onClick={() => navigate("/invoice/new")}
              className="mt-4 bg-[#1B3B2B] hover:bg-[#254F3A] text-white rounded-lg"
            >
              <Plus className="h-4 w-4 mr-1" /> Create Invoice
            </Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {invoices.map((inv) => (
              <button
                key={inv.id}
                data-testid="invoice-list-item"
                onClick={() => navigate(`/invoice/${inv.id}`)}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 hover:border-[#1B3B2B]/40 hover:shadow-sm transition-all group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-data text-xs font-semibold text-slate-400">{inv.invoice_number}</span>
                    <StatusPill inv={inv} />
                  </div>
                  <p className="font-semibold text-slate-900 mt-1 truncate">{inv.customer?.name || "Unnamed"}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {serviceLabel(inv.service_type)} · {fmtDate(inv.service_date)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p data-testid="invoice-total-display" className="font-mono-data font-bold text-lg text-[#1B3B2B]">
                    {money(inv.total)}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#1B3B2B] transition-colors" />
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Floating mobile CTA */}
      <button
        data-testid="fab-new-invoice-button"
        onClick={() => navigate("/invoice/new")}
        className="fixed bottom-5 right-5 z-30 h-14 px-5 rounded-full bg-[#D97706] hover:bg-[#b96205] text-white font-semibold shadow-xl flex items-center gap-2 transition-transform hover:scale-105 active:scale-95"
      >
        <Plus className="h-5 w-5" /> New Invoice
      </button>
    </div>
  );
}
